// Libraries
import { useMultiStyleConfig } from '@chakra-ui/react';
import { useState } from 'react';
// Components
import { useCalendar } from '@hooks/index';
// Utils
import { UseDatePickerProps } from './useDatePicker.types';
import { format } from 'date-fns-tz';

/**
 * Custom hook for managing a date picker component.
 *
 * @param {UseDatePickerProps} props - The properties for this custom hook.
 * @param {number[]} props.availableYears - An array of available years for the date picker.
 * @param {number} props.weekStartsOn - The day on which the week starts (0 for Sunday, 1 for Monday, etc.).
 * @param {number} props.numberOfMonths - The number of months to display in the date picker.
 * @param {Date} props.minDate - The minimum selectable date.
 * @param {Date} props.maxDate - The maximum selectable date.
 * @param {Date | Date[]} props.selected - The initially selected date or dates.
 * @param {function} props.onSelected - A callback function to handle when a date is selected.
 * @param {Date} props.defaultDate - The default date to display if there are no initial selections.
 * @param {string} props.queryParam - The query parameter name for selected dates in the URL.
 * @returns {object} An object containing various date picker state properties and functions.
 */
export const useDatePicker = (props: UseDatePickerProps) => {
  // Destructure the props
  const {
    availableYears,
    weekStartsOn,
    numberOfMonths,
    startDate,
    endDate,
    selected: initialDates,
    onSelected,
    shortcutDate,
    queryParam,
    shortcutDateLabel,
  } = props;

  // Manage the state of the date picker's open/close state
  const [isOpen, setIsOpen] = useState(false);

  // Retrieve necessary values from the useCalendar hook
  const {
    getAvailableYears,
    viewing,
    currentView,
    months,
    inRange,
    isSelected,
    toggle,
    select,
    viewMonth,
    viewYear,
    selected,
  } = useCalendar({
    availableYears,
    weekStartsOn,
    numberOfMonths,
    startDate,
    endDate,
    selected: initialDates,
    viewing: (initialDates && initialDates[0]) || shortcutDate,
  });

  // Retrieve the styles for the DatePicker using the useMultiStyleConfig hook
  const styles = useMultiStyleConfig('DatePicker');

  // Define the pickerParams object to pass to CalendarLayout component
  const pickerParams = {
    getAvailableYears,
    months,
    inRange,
    viewing,
    viewMonth,
    currentView,
    isSelected,
    toggle,
    viewYear,
    select,
    styles,
    shortcutDate,
    onSelected,
    queryParam,
    shortcutDateLabel,
  };

  const togglePicker = (state?: boolean) => {
    setIsOpen((isOpen) => state ?? !isOpen);
  };

  const border = isOpen ? 'selected' : 'section';
  const boxShadow = isOpen ? 'inputActive' : '';
  const value = selected[0] ? format(selected[0], 'MMM d, yyyy') : '';
  return {
    pickerParams,
    togglePicker,
    border,
    boxShadow,
    toggle,
    value,
    isOpen,
  };
};
